import { useState, useEffect } from "react";
import {
  Award, Users, TrendingUp, Plus, Minus, Search, RefreshCw,
  ChevronDown, ChevronUp, CreditCard, X,
} from "lucide-react";
import { useAdmin } from "../admin-context";

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const VirtualCard = ({ card, userName }) => {
  if (!card) return null;
  const active = card.status === "active";
  return (
    <div className={`relative rounded-2xl p-5 text-white overflow-hidden select-none ${
      active
        ? "bg-gradient-to-br from-orange-500 via-orange-400 to-red-500 shadow-lg"
        : "bg-gradient-to-br from-gray-400 to-gray-500"
    }`}>
      <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 rounded-full" />
      <div className="absolute -bottom-8 -left-4 w-36 h-36 bg-white/10 rounded-full" />
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="text-[10px] font-semibold opacity-70 uppercase tracking-widest">Gamapa Loyalty</p>
            <p className="text-sm font-bold mt-0.5">Virtual Card</p>
          </div>
          <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-white/20">{active ? "ACTIVE" : "BLOCKED"}</span>
        </div>
        <p className="font-mono text-lg tracking-widest font-bold mb-5">{card.card_number}</p>
        <div className="flex justify-between items-end">
          <div>
            <p className="text-[10px] opacity-60 uppercase">Card Holder</p>
            <p className="text-sm font-bold truncate max-w-[180px]">{userName || "—"}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] opacity-60 uppercase">Member Since</p>
            <p className="text-sm font-bold">{card.created_at ? new Date(card.created_at).getFullYear() : new Date().getFullYear()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoyaltyAdminPage = () => {
  const { users, getUserLoyalty, awardPoints, updateCardStatus, showToast } = useAdmin();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [awardForm, setAwardForm] = useState({ points: "", description: "" });
  const [saving, setSaving] = useState(false);

  const filteredUsers = (users || []).filter((u) =>
    !search || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const openUser = async (user) => {
    setSelected(user);
    setLoyalty(null);
    setShowHistory(false);
    setDrawerOpen(true);
    setLoyaltyLoading(true);
    const data = await getUserLoyalty(user.id);
    setLoyalty(data);
    setLoyaltyLoading(false);
  };

  const handleAward = async () => {
    if (!awardForm.points || isNaN(Number(awardForm.points))) return showToast("Enter valid points", "error");
    setSaving(true);
    await awardPoints(selected.id, Number(awardForm.points), awardForm.description);
    const data = await getUserLoyalty(selected.id);
    setLoyalty(data);
    setSaving(false);
    setAwardForm({ points: "", description: "" });
    showToast(Number(awardForm.points) >= 0 ? "Points awarded" : "Points deducted");
  };

  const handleToggleCard = async () => {
    if (!loyalty?.card) return;
    const newStatus = loyalty.card.status === "active" ? "blocked" : "active";
    await updateCardStatus(loyalty.card.id, newStatus);
    const data = await getUserLoyalty(selected.id);
    setLoyalty(data);
    showToast(`Card ${newStatus}`);
  };

  const totalPoints = (users || []).reduce((_, __) => _, 0); // placeholder — would need aggregate endpoint

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Loyalty Program</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage customer loyalty cards and points</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><Users size={20} className="text-orange-600" /></div>
          <div>
            <p className="text-xs text-gray-500">Enrolled Members</p>
            <p className="text-2xl font-bold text-gray-900">{(users || []).length}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Award size={20} className="text-purple-600" /></div>
          <div>
            <p className="text-xs text-gray-500">Click a customer to view their card</p>
            <p className="text-sm font-medium text-gray-600 mt-0.5">Points + virtual card management</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers..."
          className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
      </div>

      {/* Customer list */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {["Customer", "Email", "Joined", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredUsers.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-gray-400">No customers found</td></tr>
            ) : filteredUsers.map((u) => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{u.firstName} {u.lastName}</td>
                <td className="px-4 py-3 text-gray-500">{u.email}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openUser(u)} className="px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg text-xs font-semibold flex items-center gap-1 ml-auto">
                    <CreditCard size={13} /> View Card
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Loyalty Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={`${selected?.firstName || ""} ${selected?.lastName || ""} — Loyalty`}>
        {loyaltyLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">Loading...</div>
        ) : loyalty ? (
          <div className="space-y-5">
            <VirtualCard card={loyalty.card} userName={`${selected?.firstName} ${selected?.lastName}`} />

            <div className="bg-orange-50 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 font-semibold uppercase tracking-wide">Points Balance</p>
                <p className="text-3xl font-extrabold text-orange-700">{loyalty.balance ?? 0}</p>
              </div>
              <Award size={36} className="text-orange-300" />
            </div>

            {loyalty.card && (
              <button onClick={handleToggleCard}
                className={`w-full py-2 rounded-lg text-sm font-semibold border transition ${
                  loyalty.card.status === "active"
                    ? "border-red-200 text-red-600 hover:bg-red-50"
                    : "border-green-200 text-green-700 hover:bg-green-50"
                }`}>
                {loyalty.card.status === "active" ? "Block Card" : "Activate Card"}
              </button>
            )}

            {/* Award/Deduct */}
            <div className="border rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-800">Adjust Points</p>
              <input type="number" value={awardForm.points} onChange={(e) => setAwardForm((f) => ({ ...f, points: e.target.value }))}
                placeholder="e.g. 100 or -50"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
              <input type="text" value={awardForm.description} onChange={(e) => setAwardForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Reason (optional)"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
              <button onClick={handleAward} disabled={saving}
                className="w-full py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg text-sm disabled:opacity-60">
                {saving ? "Saving..." : "Apply Adjustment"}
              </button>
            </div>

            {/* Transaction history */}
            <div>
              <button onClick={() => setShowHistory((s) => !s)}
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 w-full">
                {showHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                Transaction History ({(loyalty.transactions || []).length})
              </button>
              {showHistory && (
                <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                  {(loyalty.transactions || []).length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No transactions yet</p>
                  ) : loyalty.transactions.map((t) => (
                    <div key={t.id} className="flex items-start justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="text-xs font-medium text-gray-800">{t.description || t.type}</p>
                        <p className="text-xs text-gray-400">{new Date(t.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`text-sm font-bold ${Number(t.points) >= 0 ? "text-green-600" : "text-red-500"}`}>
                        {Number(t.points) >= 0 ? "+" : ""}{t.points}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-16 text-gray-400">No loyalty data found</div>
        )}
      </Drawer>
    </div>
  );
};

export default LoyaltyAdminPage;
